// ============================================================================
// Annual Report pack — the documents and their default order (ADR-0024)
// ============================================================================
// Built from the engagement's report rows each time the screen opens; the
// auditor ticks and reorders at print time (nothing is saved). The AGM Notice
// row becomes three documents — the Notice at the front, the proxy form and
// attendance slip at the back. No API imports, so jest runs this.

import type { ReportListItem } from 'api/types/fsa/reportData.types';

export type PackPartKind = 'FINANCIAL_STATEMENTS' | 'REPORT' | 'AGM_NOTICE' | 'AGM_PROXY' | 'AGM_ATTENDANCE';

export interface PackPart {
  /** Stable key for the list. */
  key: string;
  kind: PackPartKind;
  title: string;
  reportId?: string;
  reportCode?: string;
  included: boolean;
  /** Shown beside the title: why it starts unticked, or what to check. */
  note?: string;
}

/** Filed with the annual return rather than the annual report — offered, not ticked. */
const LIST_CODES = new Set(['MEMBERS_LIST', 'SHARE_MOVEMENTS', 'DIRECTORS_LIST']);

const RANK: Record<string, number> = {
  AGM_NOTICE: 10,
  DIRECTORS_REPORT: 20,
  AUDIT_REPORT: 30,
  CARO: 40,
  // other narrative reports: 45
  FINANCIAL_STATEMENTS: 50,
  MEMBERS_LIST: 60,
  SHARE_MOVEMENTS: 61,
  DIRECTORS_LIST: 62,
  AGM_PROXY: 90,
  AGM_ATTENDANCE: 95,
};

const PRINTED_TITLE: Record<string, string> = {
  AGM_NOTICE: 'Notice of Annual General Meeting',
  AGM_PROXY: 'Proxy Form (MGT-11)',
  AGM_ATTENDANCE: 'Attendance Slip',
  FINANCIAL_STATEMENTS: 'Financial Statements',
};

/** The default pack: every applicable, installed document, in annual-report order. */
export function defaultPackParts(reports: ReportListItem[]): PackPart[] {
  const ranked: Array<PackPart & { rank: number; order: number }> = [];
  ranked.push({
    key: 'fs', kind: 'FINANCIAL_STATEMENTS', title: PRINTED_TITLE.FINANCIAL_STATEMENTS, included: true,
    rank: RANK.FINANCIAL_STATEMENTS, order: 0,
  });

  for (const r of reports) {
    if (!r.isInstalled) continue;
    const code = (r.reportCode ?? '').toUpperCase();
    const notApplicable = r.isExcluded;
    if (code === 'AGM_NOTICE') {
      (['AGM_NOTICE', 'AGM_PROXY', 'AGM_ATTENDANCE'] as const).forEach(kind => ranked.push({
        key: kind, kind, title: PRINTED_TITLE[kind], reportCode: code,
        included: !notApplicable, note: notApplicable ? 'not applicable' : undefined,
        rank: RANK[kind], order: r.displayOrder,
      }));
      continue;
    }
    const isList = LIST_CODES.has(code);
    ranked.push({
      key: `report:${r.reportId}`, kind: 'REPORT', title: r.reportName, reportId: String(r.reportId), reportCode: code,
      included: !notApplicable && !isList,
      note: notApplicable ? 'not applicable' : isList ? 'usually filed with the annual return' : !r.hasSavedData ? 'not saved' : undefined,
      rank: RANK[code] ?? 45, order: r.displayOrder,
    });
  }

  return ranked
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .map(({ rank: _rank, order: _order, ...p }) => p);
}

/** Moves a document one place up or down. */
export function movePart(parts: PackPart[], key: string, delta: -1 | 1): PackPart[] {
  const i = parts.findIndex(p => p.key === key);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= parts.length) return parts;
  const next = parts.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}
